// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The market_context consumption hook (Track 1d). Calls get_current_market_context(
// _period_label, _period_type) — see docs/investment-reporting/0003d_market_context.sql §9a
// and market-context-slot-v0.md §6. The RPC returns a TYPED jsonb envelope, never a bare
// slot and never null-for-absent:
//
//   * { status:'published', market_context:<slot>, run_id, regime_label, schema_version,
//       curated_by/at, parent_run_id, parent_status }
//   * { status:'no_published_slot', market_context:null, detail:'…' }
//
// This hook does NOT paper over the "no published slot" sentinel — the caller (
// YardeniSecondOpinion) must render it as an explicit empty state, never fall back to a
// stale prior-quarter regime (never-fabricate). `isMarketContextPublished` narrows the
// envelope so a consumer can never accidentally read `.market_context` off the absent case.
//
// NOTE on types: the market_context Cell's provenance.engine set is ADDITIVELY widened in
// 0.2.0 (+vendor_actual, +vendor_chart_verified) plus two new provenance keys
// (chart_read_id, market_series_id). The two 0.2.0 engine MEMBERS are now carried in the
// shared src/types/ir.ts `ProvenanceEngine` union (the canonical 7-member set). The LOCAL
// `MarketContextCell`/`MarketContextProseSlot` mirrors below are retained only because the
// shared strict `Provenance` type does not yet enumerate the two new provenance KEYS
// (chart_read_id, market_series_id); consumers pass them through `CellValue`/`ProseSlotBlock`
// via a narrow `as unknown as Cell` cast at the render boundary — the same defensive-cast
// convention already used for `(supabase as any).rpc(...)` elsewhere in this shell.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Cell, Confidence, ProseSlot } from "@/types/ir";

// ----------------------------------------------------------------------------
// Local 0.2.0 engine union — a subset of the shared 7-member ProvenanceEngine (ir.ts), kept
// local only to pair with the mirror types below; the two 0.2.0 members now live in ir.ts.
// ----------------------------------------------------------------------------
export type MarketContextEngine =
  | "web_verified_market"
  | "vendor_actual" // NEW 0.2.0 — S&P DJI actuals workbook figure
  | "vendor_chart_verified" // NEW 0.2.0 — human-verified Yardeni/FactSet chart read
  | "manual_locked"
  | "deterministic_compute";

export interface MarketContextProvenance {
  engine: MarketContextEngine;
  source_ref: string;
  inputs?: string[];
  computed_at?: string;
  market_series_id?: string | null; // NEW 0.2.0 — web_verified_market | vendor_actual
  chart_read_id?: string | null; // NEW 0.2.0 — REQUIRED when engine = vendor_chart_verified
}

// Structurally mirrors `Cell` (types/ir.ts) but with the widened 0.2.0 provenance. Cast
// `as unknown as Cell` at the CellValue/ProseSlotBlock boundary (see file header).
export interface MarketContextCell {
  value: number | null;
  basis: string;
  status: "ok" | "not_found" | "quarantined_forward_read" | "temporal_quarantine";
  provenance: MarketContextProvenance;
  confidence: Confidence;
  needs_review: boolean;
  flag_ids: string[];
  note: string | null;
  author?: never;
  text?: never;
}

export function asCell(c: MarketContextCell | undefined): Cell | undefined {
  return c as unknown as Cell | undefined;
}

// Structurally mirrors `ProseSlot` (types/ir.ts) but additively widened with an optional
// `verdict` so a real (non-legacy) scorecard row can still surface the confirm/nuance/overturn
// badge alongside its prose, per the slot contract's `scorecard.rows: ProseSlot[]` (§4). Narrow
// a MarketContextScorecardRow to this via the shared `isProseSlot` (types/ir.ts) — a value
// assignable to ProseSlot is assignable to MarketContextProseSlot.
export interface MarketContextProseSlot extends ProseSlot {
  verdict?: "confirm" | "nuance" | "overturn";
}

// The OLD ScorecardRow shape (matches types/ir.ts `ScorecardRow`) — market-context-slot-v0.md
// §3 shows a scorecard row example in this shape even though §4's slot skeleton describes
// `scorecard.rows` as `ProseSlot[]`. Rendered defensively: a row may arrive in EITHER shape.
export interface LegacyScorecardRow {
  cbam_claim: string;
  verdict: "confirm" | "nuance" | "overturn";
  evidence: string;
  confidence: Confidence;
  adversarially_verified: boolean;
  narrative_ref: string;
  text?: never;
  cell_refs?: never;
}

export type MarketContextScorecardRow = MarketContextProseSlot | LegacyScorecardRow;

/**
 * One headline market figure. The numeric Cell sits under the figure's PERIOD KEY — `q2` on a
 * quarterly slot, `m1` on a monthly one — or inline on the figure itself.
 *
 * ⚠️ `q2` USED TO BE DECLARED AND READ AS A REQUIRED KEY HERE, and that was a defect, not a
 * simplification. `reporting-jpm-market-review` stamps monthly Cells `m1`
 * (supabase/functions/reporting-jpm-market-review/index.ts:182, `runType === "monthly_delta" ?
 * "m1" : "q2"`), so on a monthly slot `fig.q2` is `undefined` and every figure rendered as an
 * em-dash — a published, lineage-valid number displayed as absent. The DB-side twin of this bug
 * (yardeni_assert_market_context_lineage looking for the Cell only under 'q2', and REFUSING the
 * whole publish when it was keyed 'm1') was fixed in migration 20260820184002; this is the read
 * half of the same fix.
 *
 * Neither half special-cases 'm1'. Special-casing whichever period is in front of you is how the
 * q2 hardcoding got here in the first place — use `marketFigureCell()` below, which locates the
 * Cell by SHAPE.
 */
export interface MarketContextMarketFigure {
  label: string;
  basis?: string;
  range?: string;
  note?: string;
  /** The Cell under its period key (`q2`, `m1`, `m6`, `y1`, …), or inline on the figure. */
  [periodKey: string]: unknown;
}

/**
 * Locate a market figure's numeric Cell, whatever period it is keyed under.
 *
 * Mirrors the server-side rule in `yardeni_assert_market_context_lineage` exactly: a Cell is any
 * value that is an object carrying `provenance`, and a figure may instead BE the Cell (inline
 * shape). Returns undefined when the figure carries none, which renders honestly as absent.
 *
 * Where a figure carries more than one period Cell, the slot's own `basis` is preferred when it
 * names one, then the conventional period order, so the displayed number is deterministic rather
 * than dependent on JSON key order.
 */
export function marketFigureCell(
  fig: MarketContextMarketFigure | undefined,
): MarketContextCell | undefined {
  if (!fig || typeof fig !== "object") return undefined;

  const isCell = (v: unknown): v is MarketContextCell =>
    !!v && typeof v === "object" && !Array.isArray(v) && "provenance" in (v as object);

  // Inline shape: the figure IS the Cell.
  if (isCell(fig)) return fig as unknown as MarketContextCell;

  const cellKeys = Object.keys(fig).filter((k) => isCell(fig[k]));
  if (cellKeys.length === 0) return undefined;
  if (cellKeys.length === 1) return fig[cellKeys[0]] as MarketContextCell;

  const basis = typeof fig.basis === "string" ? fig.basis.toLowerCase() : "";
  const preferred = ["m1", "q2", "m6", "y1"].find(
    (p) => cellKeys.includes(p) && basis.includes(`/${p}/`),
  );
  if (preferred) return fig[preferred] as MarketContextCell;

  const conventional = ["q2", "m1", "m6", "y1"].find((p) => cellKeys.includes(p));
  return fig[conventional ?? cellKeys[0]] as MarketContextCell;
}

export interface MarketContextForwardReadStat {
  value: MarketContextCell; // status: quarantined_forward_read — panel-only
  label: string;
  sub?: string;
}

export interface MarketContextSlot {
  schema_version: string;
  scorecard: {
    counts: { confirm: number; nuance: number; overturn: number };
    rows: MarketContextScorecardRow[];
    overturned: MarketContextScorecardRow[];
  };
  market_figures: MarketContextMarketFigure[];
  regime: {
    message: string;
    source: string;
    callouts: { title: string; body: string }[];
  };
  cohorts: { value: string; label: string; sub?: string }[];
  forward_read: { label: string; stats: MarketContextForwardReadStat[] };
  scenarios: { scenario: string; prob: string; meaning: string; action: string }[];
  lean_in: { theme: string; value_read: string; runway: string; cbam_action: string; ic_only: boolean }[];
  source: string;
  distribution: { retail_public: boolean };
}

export type MarketContextParentStatus = "n/a" | "resolved" | "missing" | "unpublished";

export interface MarketContextEnvelopePublished {
  status: "published";
  period_label: string;
  period_type: string;
  run_type: "quarterly_full" | "monthly_delta";
  run_id: string;
  schema_version: string;
  regime_label: string | null;
  as_of_date: string | null;
  curated_by: string | null;
  curated_at: string | null;
  parent_run_id: string | null;
  parent_status: MarketContextParentStatus;
  market_context: MarketContextSlot;
}

export interface MarketContextEnvelopeAbsent {
  status: "no_published_slot";
  period_label: string;
  period_type: string;
  run_type: string | null;
  market_context: null;
  detail: string;
}

export type MarketContextEnvelope = MarketContextEnvelopePublished | MarketContextEnvelopeAbsent;

export function isMarketContextPublished(
  envelope: MarketContextEnvelope | undefined,
): envelope is MarketContextEnvelopePublished {
  return !!envelope && envelope.status === "published" && envelope.market_context != null;
}

// period_type maps: quarterly-full -> quarterly_full, monthly-interim -> monthly_delta (server-side).
// Pass the reporting_cycles period_type verbatim ('quarterly-full' | 'monthly-interim').
export function useMarketContext(periodLabel: string | undefined, periodType: string | undefined) {
  return useQuery({
    queryKey: ["get_current_market_context", periodLabel, periodType],
    enabled: !!periodLabel && !!periodType,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<MarketContextEnvelope> => {
      const { data, error } = await (supabase as any).rpc("get_current_market_context", {
        _period_label: periodLabel,
        _period_type: periodType,
      });
      if (error) throw error;
      // The RPC always returns a typed envelope (never bare null) — but stay defensive
      // against a transport-level null so the caller never mistakes it for "published".
      if (!data) {
        return {
          status: "no_published_slot",
          period_label: periodLabel ?? "",
          period_type: periodType ?? "",
          run_type: null,
          market_context: null,
          detail: "The market_context RPC returned no envelope — treating as no published slot (fail-closed).",
        };
      }
      return data as MarketContextEnvelope;
    },
  });
}
